'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { PricingEngine, PriceAlert } from '@/lib/pricing'

export default function PriceAlertsPage() {
  const [alerts, setAlerts] = useState<PriceAlert[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadAlerts()
  }, [])

  async function loadAlerts() {
    try {
      setLoading(true)
      const userAlerts = await PricingEngine.getPriceAlerts()
      setAlerts(userAlerts)
    } catch (error) {
      console.error('Failed to load alerts:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              🔔 Price Alerts
            </h1>
            <p className="text-gray-600 mt-1">
              Get notified when your watched products drop in price
            </p>
          </div>

          <div className="p-6">
            {alerts.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📈</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No price alerts yet
                </h3>
                <p className="text-gray-600 mb-6">
                  Browse products and set up price alerts to get notified of deals
                </p>
                <Link
                  href="/browse"
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Browse Products
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {alerts.map((alert) => (
                  <div
                    key={alert.id}
                    className="bg-gray-50 rounded-lg p-4 border flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-medium text-gray-900">
                          Product ID: {alert.productId}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          alert.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {alert.isActive ? 'Active' : 'Triggered'}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>
                          <span className="font-medium">Target Price:</span> ${alert.targetPrice}
                        </p>
                        <p>
                          <span className="font-medium">Current Price:</span> ${alert.currentPrice}
                        </p>
                        <p>
                          <span className="font-medium">Alert Type:</span> {
                            alert.alertType === 'drop_below' ? 'Drop Below Target' :
                            alert.alertType === 'drop_percentage' ? 'Percentage Drop' :
                            'Back In Stock'
                          }
                        </p>
                        <p>
                          <span className="font-medium">Created:</span> {alert.createdAt.toLocaleDateString()}
                        </p>
                        {alert.triggeredAt && (
                          <p>
                            <span className="font-medium">Triggered:</span> {alert.triggeredAt.toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {alert.currentPrice <= alert.targetPrice && alert.isActive && (
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                          🎉 Target Reached!
                        </span>
                      )}
                      
                      <Link
                        href={`/product/${alert.productId}`}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        View Product
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* How It Works */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            How Price Alerts Work
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl mb-2">👀</div>
              <h3 className="font-medium text-gray-900 mb-2">Watch Products</h3>
              <p className="text-sm text-gray-600">
                Set price alerts on any product page by clicking the "Set Alert" button
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">📈</div>
              <h3 className="font-medium text-gray-900 mb-2">Track Prices</h3>
              <p className="text-sm text-gray-600">
                We monitor prices across multiple retailers and track historical trends
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">🔔</div>
              <h3 className="font-medium text-gray-900 mb-2">Get Notified</h3>
              <p className="text-sm text-gray-600">
                Receive browser notifications when your target price is reached
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}